import { createContext, useContext, useState, useEffect } from 'react';
import GitHubClient from '../api/GitHubClient';
import FileLoaderService from '../api/FileLoaderService';
import TwinCATParserService from '../api/TwinCATParserService';
import { isTwinCATFile } from '../constants/fileExtensions';
import { processDroppedItems, processUploadedFiles } from '../utils/fileSystem';
import { trackLocalFilesUploaded, trackError } from '../utils/analyticsEvents';

const FileContext = createContext();

export const FileProvider = ({ children }) => {
  // GitHub configuration
  const [githubLink, setGithubLink] = useState('');
  const [githubNameAndRepo, setGithubNameAndRepo] = useState('');
  const [githubAccessToken, setGithubAccessToken] = useState('');
  const [githubBranch, setGithubBranch] = useState('');

  // File management
  const [localFiles, setLocalFiles] = useState([]);
  const [source, setSource] = useState('github');

  // Content display
  const [filePath, setFilePath] = useState('');
  const [fileName, setFileName] = useState('');
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [focusedFilePath, setFocusedFilePath] = useState(null);
  const [selectedFilePath, setSelectedFilePath] = useState(null);

  // Read URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlParam = params.get('url');
    if (urlParam) {
      setGithubLink(urlParam);
      setSource('github');
    }
  }, []);

  // Clear state when githubLink is cleared
  useEffect(() => {
    if (githubLink === '') {
      setGithubNameAndRepo('');
      setGithubBranch('');
      setFocusedFilePath(null);
      setSelectedFilePath(null);
    }
  }, [githubLink]);

  // Sync state to URL
  useEffect(() => {
    if (source === 'github' && githubNameAndRepo && githubLink) {
      let newUrl = `https://github.com${githubNameAndRepo}`;
      
      if (githubBranch) {
        const path = selectedFilePath || focusedFilePath;
        if (path) {
          // If it looks like a file (has extension) use blob, else tree
          const isFile = path.includes('.');
          newUrl += `/${isFile ? 'blob' : 'tree'}/${githubBranch}/${path}`;
        } else {
          newUrl += `/tree/${githubBranch}`;
        }
      }

      const currentUrl = new URL(window.location.href);
      if (currentUrl.searchParams.get('url') !== newUrl) {
        currentUrl.searchParams.set('url', newUrl);
        window.history.replaceState({}, '', currentUrl.toString());
      }
    } else if (source === 'local') {
      const currentUrl = new URL(window.location.href);
      if (currentUrl.searchParams.has('url')) {
        currentUrl.searchParams.delete('url');
        window.history.replaceState({}, '', currentUrl.pathname);
      }
    }
  }, [source, githubNameAndRepo, githubBranch, selectedFilePath, focusedFilePath]);

  // Services
  const githubClient = new GitHubClient(githubAccessToken);
  const fileLoaderService = new FileLoaderService(githubClient);
  const parserService = new TwinCATParserService();

  /**
   * Handle drag and drop file/folder operations
   */
  const handleFilesAndFolders = async (items) => {
    const droppedFiles = await processDroppedItems(items);
    setLocalFiles(prev => [...prev, ...droppedFiles]);
    setSource('local');
    trackLocalFilesUploaded();
  };

  /**
   * Handle file upload from input element
   */
  const handleLocalUpload = (filesList) => {
    const filesArray = processUploadedFiles(filesList);
    setLocalFiles(filesArray);
    setSource('local');
    trackLocalFilesUploaded();
  };

  /**
   * Clear all local files
   */
  const clearLocalFiles = () => {
    setLocalFiles([]);
  };

  /**
   * Load and parse a file
   */
  const loadFile = async (node) => {
    setIsLoading(true);
    try {
      setFileName(node.name);
      setFilePath(node.path);

      let decoded = '';
      if (source === 'local') {
        decoded = await fileLoaderService.loadFromLocal(node.fileRef);
      } else {
        decoded = await fileLoaderService.loadFromGithub(
          githubNameAndRepo,
          node.path,
          githubBranch
        );
      }

      let parsedContent = {};
      if (isTwinCATFile(node.path)) {
        parsedContent = parserService.parseTwinCAT(decoded);
      } else {
        parsedContent = parserService.parseGeneric(decoded);
      }

      setContent(parsedContent);
    } catch (error) {
      console.error('Error loading file:', error);
      setContent({ xmlString: `Error loading file: ${error.message}` });
      trackError('File Load Error');
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    // GitHub configuration
    githubLink,
    setGithubLink,
    githubNameAndRepo,
    setGithubNameAndRepo,
    githubAccessToken,
    setGithubAccessToken,
    githubBranch,
    setGithubBranch,
    // File management
    localFiles,
    setLocalFiles,
    source,
    setSource,
    handleFilesAndFolders,
    handleLocalUpload,
    clearLocalFiles,
    // Content display
    filePath,
    setFilePath,
    fileName,
    setFileName,
    content,
    isLoading,
    focusedFilePath,
    setFocusedFilePath,
    selectedFilePath,
    setSelectedFilePath,
    // Actions
    loadFile
  };

  return <FileContext.Provider value={value}>{children}</FileContext.Provider>;
};

/**
 * Hook to access file context
 */
export const useFile = () => {
  const context = useContext(FileContext);
  if (!context) {
    throw new Error('useFile must be used within a FileProvider');
  }
  return context;
};